// src/components/EmailIntakeSection.jsx
// ============================================================================
// Email Intake (Needs Review) section for InquiriesPage
// Shows forwarded Gmail emails parsed into inquiry drafts.
// Admin: can approve/reject. Supervisor: view only, no action buttons.
// ============================================================================

import React, { useState, useCallback } from 'react';
import {
  Mail, Check, X, ChevronDown, ChevronUp,
  AlertTriangle, Paperclip, RefreshCw, ExternalLink,
} from 'lucide-react';

// ── Styles (matches InquiriesPage BRIO patterns) ────────────────────────
const SECTION_STYLE = {
  margin: '0 0 8px',
  border: '1px solid var(--brio-border, #e2e8f0)',
  borderRadius: '0',
  background: '#fff',
  overflow: 'hidden',
};
const HEADER_STYLE = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '8px 12px',
  background: 'var(--brio-bg-subtle, #fafaf6)',
  borderBottom: '1px solid var(--brio-border, #e2e8f0)',
  cursor: 'pointer', userSelect: 'none',
};
// AUDIT[08]: Custom badge with hardcoded #d97706 and borderRadius:9px — use BrioBadge variant="warning"
const BADGE = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: '18px', height: '18px', padding: '0 5px', borderRadius: '9px',
  fontSize: '10px', fontWeight: 700, color: '#fff', background: '#d97706', marginLeft: '6px',
};
// AUDIT[01]: All confidence colors are hardcoded Tailwind-derived values — map to BRIO tokens
const CONF_COLORS = {
  high:   { bg: 'rgba(34,197,94,0.12)',  text: '#16a34a', border: '#22c55e' },  // → var(--brio-green), var(--brio-green-light)
  medium: { bg: 'rgba(249,115,22,0.12)', text: '#ea580c', border: '#fb923c' },  // → var(--brio-orange), var(--brio-orange-light)
  low:    { bg: 'rgba(239,68,68,0.12)',  text: '#dc2626', border: '#fca5a5' },  // → var(--brio-red), var(--brio-red-light)
};
const confLevel = (c) => c >= 0.7 ? 'high' : c >= 0.5 ? 'medium' : 'low';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';

// ═══════════════════════════════════════════════════════════════════════════
export default function EmailIntakeSection({
  intakeMessages = [],
  loading = false,
  isAdmin = false,         // true only for role='admin'
  onApprove,               // (intakeId) => Promise<{success, error?}>
  onReject,                // (intakeId, reason) => Promise<{success, error?}>
  onRefresh,               // () => Promise (refetch from database)
  getAttachmentUrl,        // (storagePath) => Promise<{success, url?, error?}>
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const needsReview = intakeMessages.filter((m) => m.parseStatus === 'needs_review');

  const handleApprove = useCallback(async (id) => {
    if (!onApprove) return;
    setActionLoading(id);
    const res = await onApprove(id);
    setActionLoading(null);
    if (!res.success) alert('Approve failed: ' + res.error);
    else if (expandedId === id) setExpandedId(null);
  }, [onApprove, expandedId]);

  const handleReject = useCallback(async (id) => {
    if (!onReject) return;
    setActionLoading(id);
    const res = await onReject(id, rejectReason);
    setActionLoading(null);
    setRejectingId(null);
    setRejectReason('');
    if (!res.success) alert('Reject failed: ' + res.error);
    else if (expandedId === id) setExpandedId(null);
  }, [onReject, rejectReason, expandedId]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    if (onRefresh) await onRefresh();
    setRefreshing(false);
  }, [onRefresh]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
    setRejectingId(null);
    setRejectReason('');
  };

  // Hide section entirely when empty and not loading
  if (needsReview.length === 0 && !loading) return null;

  return (
    <div style={SECTION_STYLE}>
      {/* ── Section header ──────────────────────────────────────────── */}
      <div style={HEADER_STYLE} onClick={() => setCollapsed((c) => !c)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Mail style={{ width: '14px', height: '14px', color: '#d97706' }} />
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--brio-text, #1e293b)' }}>
            Email Intake
          </span>
          {needsReview.length > 0 && <span style={BADGE}>{needsReview.length}</span>}
          <span style={{ fontSize: '10px', color: 'var(--brio-muted, #94a3b8)', marginLeft: '4px' }}>
            Needs Review
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {/* Check Gmail button — admin only for ingest trigger, all roles for refresh */}
          <button
            onClick={(e) => { e.stopPropagation(); handleRefresh(); }}
            disabled={refreshing}
            title="Refresh intake list"
            style={{
              display: 'flex', alignItems: 'center', gap: '3px',
              padding: '3px 8px', fontSize: '10px', fontWeight: 500,
              border: '1px solid var(--brio-border, #d1d5db)', borderRadius: '0',
              background: '#fff', cursor: 'pointer', color: 'var(--brio-text)',
            }}
          >
            <RefreshCw style={{
              width: '11px', height: '11px',
              animation: refreshing ? 'spin 1s linear infinite' : 'none',
            }} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          {collapsed
            ? <ChevronDown style={{ width: '14px', height: '14px', color: 'var(--brio-muted)' }} />
            : <ChevronUp style={{ width: '14px', height: '14px', color: 'var(--brio-muted)' }} />}
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────── */}
      {!collapsed && (
        <div>
          {loading && needsReview.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--brio-muted)', fontSize: '11px' }}>
              Loading intake messages…
            </div>
          )}

          {needsReview.map((msg) => {
            const pr = msg.parseResult || {};
            const conf = pr.confidence || 0;
            const cl = confLevel(conf);
            const cc = CONF_COLORS[cl];
            const warnings = pr.warnings || [];
            const isExpanded = expandedId === msg.id;
            const isRejecting = rejectingId === msg.id;
            const isLoading = actionLoading === msg.id;

            return (
              <div key={msg.id} style={{ borderBottom: '1px solid var(--brio-border, #f1f5f9)' }}>
                {/* ── Summary row ──────────────────────────────────── */}
                <div
                  onClick={() => toggleExpand(msg.id)}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr auto',
                    alignItems: 'center', padding: '8px 12px', cursor: 'pointer',
                    background: isExpanded ? 'var(--brio-bg-subtle, #fafaf6)' : '#fff',
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'center', minWidth: 0 }}>
                    <span style={{ fontSize: '10px', color: 'var(--brio-muted)', minWidth: '65px', whiteSpace: 'nowrap' }}>
                      {fmtDate(msg.receivedAt)}
                    </span>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--brio-text)', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {pr.forward_style && pr.forward_style !== 'none'
                        ? (pr.original?.name || pr.original?.email || pr.client_email_override || '—')
                        : (msg.fromName || msg.fromEmail || '—')}
                    </span>
                    {pr.forward_style && pr.forward_style !== 'none' && (
                      <span style={{ fontSize: '9px', color: 'var(--brio-muted)', fontStyle: 'italic' }}
                        title={`Forwarded by ${pr.forwarder?.name || pr.forwarder?.email || msg.fromEmail}`}>
                        ↩ fwd
                      </span>
                    )}
                    <span style={{ fontSize: '11px', color: 'var(--brio-text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: '80px' }}>
                      {msg.subject || '(no subject)'}
                    </span>
                    {pr.project_type && (
                      <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '0', background: 'rgba(159,144,42,0.1)', color: 'var(--brio-gold, #9F902A)', fontWeight: 500 }}>
                        {pr.project_type}
                      </span>
                    )}
                    {pr.location_city && (
                      <span style={{ fontSize: '10px', color: 'var(--brio-muted)' }}>📍 {pr.location_city}</span>
                    )}
                    <span style={{
                      fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '0',
                      background: cc.bg, color: cc.text, border: `1px solid ${cc.border}`,
                    }}>
                      {Math.round(conf * 100)}%
                    </span>
                    {warnings.length > 0 && (
                      <AlertTriangle style={{ width: '12px', height: '12px', color: '#d97706' }} title={warnings.join(', ')} />
                    )}
                    {(msg.attachments || []).length > 0 && (
                      <Paperclip style={{ width: '11px', height: '11px', color: 'var(--brio-muted)' }} title={`${msg.attachments.length} attachment(s)`} />
                    )}
                  </div>

                  {/* Inline quick-actions — admin only, collapsed view */}
                  {isAdmin && !isExpanded && (
                    <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }} onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleApprove(msg.id)} disabled={isLoading} title="Approve"
                        style={{ display: 'flex', alignItems: 'center', padding: '3px 8px', fontSize: '10px', fontWeight: 600, border: '1px solid #22c55e', borderRadius: '0', background: 'rgba(34,197,94,0.08)', color: '#16a34a', cursor: 'pointer' }}>
                        <Check style={{ width: '11px', height: '11px', marginRight: '2px' }} />{isLoading ? '…' : 'OK'}
                      </button>
                      <button onClick={() => { setRejectingId(msg.id); setExpandedId(msg.id); }} disabled={isLoading} title="Reject"
                        style={{ display: 'flex', alignItems: 'center', padding: '3px 8px', fontSize: '10px', fontWeight: 600, border: '1px solid #fca5a5', borderRadius: '0', background: 'rgba(239,68,68,0.06)', color: '#dc2626', cursor: 'pointer' }}>
                        <X style={{ width: '11px', height: '11px', marginRight: '2px' }} />Reject
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Expanded detail panel ────────────────────────── */}
                {isExpanded && (
                  <div style={{ padding: '10px 16px 12px', background: '#fafaf6', borderTop: '1px solid var(--brio-border, #e2e8f0)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
                      {/* Left: extracted fields */}
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--brio-gold)', marginBottom: '6px' }}>
                          Extracted Fields
                        </div>
                        <table style={{ fontSize: '11px', width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            {[
                              ['Client Name', pr.client_name_override],
                              ['Organisation', pr.client_org_name_override],
                              ['Email', pr.client_email_override],
                              ['Phone', pr.client_phone_override],
                              ['LinkedIn', pr.client_linkedin_override],
                              ['Project Type', pr.project_type],
                              ['City', pr.location_city],
                              ['Coordinates', pr.location_coordinates],
                              ['Potential', pr.potential],
                              ['Priority', pr.priority_level],
                            ].map(([label, value]) => (
                              <tr key={label}>
                                <td style={{ padding: '2px 6px 2px 0', fontWeight: 500, color: 'var(--brio-muted)', whiteSpace: 'nowrap', width: '100px' }}>{label}</td>
                                <td style={{ padding: '2px 0', color: value ? 'var(--brio-text)' : '#d1d5db' }}>
                                  {label === 'LinkedIn' && value
                                    ? <a href={value} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: '10px', textDecoration: 'none' }}>{value}</a>
                                    : label === 'Coordinates' && value
                                      ? <a href={`https://www.google.com/maps/search/?api=1&query=${value}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: '10px', textDecoration: 'none' }}>{value} ↗</a>
                                      : (value || '—')}
                                </td>
                              </tr>
                            ))}
                            {pr.brief_notes && (
                              <tr>
                                <td style={{ padding: '2px 6px 2px 0', fontWeight: 500, color: 'var(--brio-muted)', verticalAlign: 'top' }}>Summary</td>
                                <td style={{ padding: '2px 0', color: 'var(--brio-text)', fontSize: '10px', lineHeight: '1.4' }}>
                                  {(pr.brief_notes || '').substring(0, 200)}{pr.brief_notes?.length > 200 ? '…' : ''}
                                </td>
                              </tr>
                            )}
                            {pr.next_steps && (
                              <tr>
                                <td style={{ padding: '2px 6px 2px 0', fontWeight: 500, color: 'var(--brio-muted)', verticalAlign: 'top' }}>Next Steps</td>
                                <td style={{ padding: '2px 0', color: 'var(--brio-text)', fontSize: '10px' }}>{pr.next_steps}</td>
                              </tr>
                            )}
                            {(pr.area_statements || []).length > 0 && (
                              <tr>
                                <td style={{ padding: '2px 6px 2px 0', fontWeight: 500, color: 'var(--brio-muted)', verticalAlign: 'top' }}>Area</td>
                                <td style={{ padding: '2px 0', color: 'var(--brio-text)', fontSize: '10px' }}>
                                  {pr.area_statements.map((a, i) => (
                                    <span key={i}>{a.area} {a.unit} ({a.label}){i < pr.area_statements.length - 1 ? ', ' : ''}</span>
                                  ))}
                                </td>
                              </tr>
                            )}
                            {pr.client_interesting_links_override && (
                              <tr>
                                <td style={{ padding: '2px 6px 2px 0', fontWeight: 500, color: 'var(--brio-muted)', verticalAlign: 'top' }}>Personal Notes</td>
                                <td style={{ padding: '2px 0', color: 'var(--brio-text)', fontSize: '10px', lineHeight: '1.4' }}>
                                  {pr.client_interesting_links_override}
                                </td>
                              </tr>
                            )}
                            {pr.field_evidence && Object.keys(pr.field_evidence).length > 0 && (
                              <tr>
                                <td style={{ padding: '2px 6px 2px 0', fontWeight: 500, color: 'var(--brio-muted)', verticalAlign: 'top' }}>Evidence</td>
                                <td style={{ padding: '2px 0', fontSize: '9px', color: '#9ca3af', lineHeight: '1.3' }}>
                                  {Object.entries(pr.field_evidence).map(([k, v]) => (
                                    <div key={k}><strong>{k}:</strong> {String(v).substring(0, 100)}</div>
                                  ))}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {warnings.length > 0 && (
                          <div style={{ marginTop: '8px', padding: '5px 8px', background: 'rgba(249,115,22,0.06)', borderRadius: '0', border: '1px solid rgba(249,115,22,0.2)' }}>
                            <div style={{ fontSize: '10px', fontWeight: 600, color: '#d97706', marginBottom: '2px' }}>
                              <AlertTriangle style={{ width: '10px', height: '10px', display: 'inline', marginRight: '3px', verticalAlign: 'middle' }} />
                              Parse Warnings
                            </div>
                            {warnings.map((w, i) => (
                              <div key={i} style={{ fontSize: '10px', color: '#92400e', paddingLeft: '14px' }}>• {w}</div>
                            ))}
                          </div>
                        )}

                        {/* ── Forward detection info ─────────────────── */}
                        {pr.forward_style && pr.forward_style !== 'none' && (
                          <div style={{ marginTop: '8px', padding: '6px 8px', background: 'rgba(59,130,246,0.05)', borderRadius: '0', border: '1px solid rgba(59,130,246,0.15)' }}>
                            <div style={{ fontSize: '10px', fontWeight: 600, color: '#2563eb', marginBottom: '4px' }}>
                              ↩ Forwarded Email Detected
                              <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '6px' }}>
                                ({pr.forward_style} · {Math.round((pr.forward_confidence || 0) * 100)}% confidence)
                              </span>
                            </div>
                            <table style={{ fontSize: '10px', width: '100%', borderCollapse: 'collapse' }}>
                              <tbody>
                                <tr>
                                  <td style={{ padding: '1px 6px 1px 0', fontWeight: 500, color: '#6b7280', width: '100px' }}>Forwarded by</td>
                                  <td style={{ padding: '1px 0', color: '#374151' }}>
                                    {pr.forwarder?.name ? `${pr.forwarder.name} <${pr.forwarder.email}>` : pr.forwarder?.email || '—'}
                                  </td>
                                </tr>
                                <tr>
                                  <td style={{ padding: '1px 6px 1px 0', fontWeight: 500, color: '#6b7280' }}>Original sender</td>
                                  <td style={{ padding: '1px 0', color: '#1e40af', fontWeight: 500 }}>
                                    {pr.original?.name ? `${pr.original.name} <${pr.original.email}>` : pr.original?.email || '—'}
                                  </td>
                                </tr>
                                {pr.original?.subject && (
                                  <tr>
                                    <td style={{ padding: '1px 6px 1px 0', fontWeight: 500, color: '#6b7280' }}>Original subject</td>
                                    <td style={{ padding: '1px 0', color: '#374151' }}>{pr.original.subject}</td>
                                  </tr>
                                )}
                                {pr.original?.received_at && (
                                  <tr>
                                    <td style={{ padding: '1px 6px 1px 0', fontWeight: 500, color: '#6b7280' }}>Original date</td>
                                    <td style={{ padding: '1px 0', color: '#374151' }}>{fmtDate(pr.original.received_at)} {fmtTime(pr.original.received_at)}</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                            {(pr.forward_confidence || 0) < 0.6 && (
                              <div style={{ marginTop: '4px', padding: '3px 6px', background: 'rgba(249,115,22,0.08)', borderRadius: '0', fontSize: '10px', color: '#d97706', fontWeight: 500 }}>
                                <AlertTriangle style={{ width: '10px', height: '10px', display: 'inline', marginRight: '3px', verticalAlign: 'middle' }} />
                                Original sender detection uncertain — please verify client details
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right: original email */}
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--brio-gold)', marginBottom: '6px' }}>
                          {pr.forward_style && pr.forward_style !== 'none' ? 'Gmail Envelope (forwarded)' : 'Original Email'}
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--brio-muted)', marginBottom: '4px' }}>
                          <strong>{pr.forward_style && pr.forward_style !== 'none' ? 'Forwarded by:' : 'From:'}</strong> {msg.fromName ? `${msg.fromName} <${msg.fromEmail}>` : msg.fromEmail}<br />
                          <strong>Date:</strong> {fmtDate(msg.receivedAt)} {fmtTime(msg.receivedAt)}<br />
                          <strong>Subject:</strong> {msg.subject}
                        </div>
                        <div style={{
                          maxHeight: '200px', overflowY: 'auto', padding: '6px 8px',
                          background: '#fff', border: '1px solid var(--brio-border, #e2e8f0)', borderRadius: '0',
                          fontSize: '11px', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          color: 'var(--brio-text)',
                        }}>
                          {msg.bodyText || '(empty body)'}
                        </div>

                        {(msg.attachments || []).length > 0 && (
                          <div style={{ marginTop: '6px' }}>
                            <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--brio-muted)', marginBottom: '3px' }}>
                              Attachments ({msg.attachments.length})
                            </div>
                            {msg.attachments.map((att, i) => (
                              <AttachmentLink key={i} att={att} getAttachmentUrl={getAttachmentUrl} />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Admin action bar ────────────────────────── */}
                    {isAdmin && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px', borderTop: '1px solid var(--brio-border, #e2e8f0)' }}>
                        {!isRejecting ? (
                          <>
                            <button onClick={() => handleApprove(msg.id)} disabled={isLoading}
                              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 14px', fontSize: '11px', fontWeight: 600, border: '1px solid #22c55e', borderRadius: '0', background: '#16a34a', color: '#fff', cursor: 'pointer' }}>
                              <Check style={{ width: '12px', height: '12px' }} />
                              {isLoading ? 'Approving…' : 'Approve Inquiry'}
                            </button>
                            <button onClick={() => setRejectingId(msg.id)} disabled={isLoading}
                              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 14px', fontSize: '11px', fontWeight: 600, border: '1px solid #fca5a5', borderRadius: '0', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                              <X style={{ width: '12px', height: '12px' }} />
                              Reject
                            </button>
                            <span style={{ flex: 1 }} />
                            {msg.linkedInquiryId && (
                              <span style={{ fontSize: '10px', color: 'var(--brio-muted)' }}>
                                Linked to inquiry — editable in main table after approval
                              </span>
                            )}
                          </>
                        ) : (
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <input type="text" value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Reason for rejection (optional)"
                                autoFocus
                                onKeyDown={(e) => e.key === 'Enter' && handleReject(msg.id)}
                                style={{ flex: 1, padding: '5px 8px', fontSize: '11px', border: '1px solid #fca5a5', borderRadius: '0', background: '#fff', color: 'var(--brio-text)' }} />
                              <button onClick={() => handleReject(msg.id)} disabled={isLoading}
                                style={{ padding: '5px 12px', fontSize: '11px', fontWeight: 600, border: '1px solid #dc2626', borderRadius: '0', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>
                                {isLoading ? 'Rejecting…' : 'Confirm Reject'}
                              </button>
                              <button onClick={() => { setRejectingId(null); setRejectReason(''); }}
                                style={{ padding: '5px 10px', fontSize: '11px', border: '1px solid var(--brio-border)', borderRadius: '0', background: '#fff', color: 'var(--brio-text)', cursor: 'pointer' }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Supervisor notice */}
                    {!isAdmin && (
                      <div style={{ paddingTop: '8px', borderTop: '1px solid var(--brio-border, #e2e8f0)', fontSize: '10px', color: 'var(--brio-muted)', fontStyle: 'italic' }}>
                        View only — admin approval required to confirm this inquiry
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Attachment download link ────────────────────────────────────────────
function AttachmentLink({ att, getAttachmentUrl }) {
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSignedClick = async (e) => {
    e.preventDefault();
    if (signedUrl) { window.open(signedUrl, '_blank'); return; }
    if (!getAttachmentUrl || !att.storagePath || att.storagePath.startsWith('error:')) return;
    setLoading(true);
    const res = await getAttachmentUrl(att.storagePath);
    setLoading(false);
    if (res.success) { setSignedUrl(res.url); window.open(res.url, '_blank'); }
  };

  const sizeLabel = att.size ? `(${(att.size / 1024).toFixed(0)}KB)` : '';
  const linkStyle = { display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', marginRight: '8px', textDecoration: 'none', cursor: 'pointer' };

  if (att.dropboxUrl) {
    return (
      <a href={att.dropboxUrl} target="_blank" rel="noopener noreferrer"
        style={{ ...linkStyle, color: 'var(--brio-gold, #9F902A)' }}
        title="Open in Dropbox">
        <Paperclip style={{ width: '10px', height: '10px' }} />
        {att.filename}
        <span style={{ color: 'var(--brio-muted)', fontSize: '9px' }}>{sizeLabel}</span>
        <ExternalLink style={{ width: '9px', height: '9px', color: 'var(--brio-muted)' }} />
      </a>
    );
  }

  return (
    <a href="#" onClick={handleSignedClick}
      style={{ ...linkStyle, color: 'var(--brio-gold, #9F902A)' }}>
      <Paperclip style={{ width: '10px', height: '10px' }} />
      {loading ? 'Loading…' : att.filename}
      <span style={{ color: 'var(--brio-muted)', fontSize: '9px' }}>{sizeLabel}</span>
      <ExternalLink style={{ width: '9px', height: '9px', color: 'var(--brio-muted)' }} />
    </a>
  );
}
